import { useCallback, useEffect, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import i18n from 'locales/i18n';
import trackingAPI from 'api/tracking';
import configAPI, { DEFAULT_CONFIG } from 'api/config';
import { ITrackingData, ITrackingConfig } from 'interfaces';
import { Error, Loader, Tracking } from 'components';
import MapWithTracking from 'components/tracking-map/MapWithTracking';

interface Props {
    isTestMode?: boolean;
}
const isNotExpiredTrackingLink = (dateISO: string | null) => {
    if (!dateISO) return true;
    const date = dayjs(dateISO);
    const now = dayjs();
    const diffInHours = now.diff(date, 'hour');
    const is24hours = diffInHours <= 24;
    return is24hours;
};

export const TrackingMap = ({ isTestMode = false }: Props) => {
    const trackingId: string | undefined = window.location.pathname.split('/').filter(Boolean)[0];
    const durationRef = useRef(0);
    const [status, setStatus] = useState<'pending' | 'resolved' | 'rejected'>('pending');
    const [trackingConfig, setTrackingConfig] = useState<ITrackingConfig>(DEFAULT_CONFIG);
    const [trackingData, setTrackingData] = useState<ITrackingData | undefined>();
    const [estimatedTime, setEstimatedTime] = useState<string | undefined>();

    const updateEstimatedTime = (newDuration?: number): void => {
        if (newDuration) {
            durationRef.current = newDuration;
            setEstimatedTime(dayjs().add(newDuration, 'second').format('HH:mm'));
        } else if (durationRef.current) {
            setEstimatedTime(dayjs().add(durationRef.current, 'second').format('HH:mm'));
        }
    };

    const updateTrackingData = useCallback((data: ITrackingData) => setTrackingData(data), [setTrackingData]);

    useEffect(() => {
        if (trackingData?.status === 'delivered' && estimatedTime) {
            setEstimatedTime(undefined);
        }
    }, [trackingData, estimatedTime]);

    useEffect(() => {
        configAPI.get().then(setTrackingConfig);
    }, []);

    useEffect(() => {
        if (trackingId) {
            trackingAPI
                .infos({ id: trackingId })
                .then((data) => {
                    if (isNotExpiredTrackingLink(data.dropoffEffectiveDate)) {
                        setStatus('resolved');
                        setTrackingData(data);
                    } else {
                        setStatus('rejected');
                    }
                })
                .catch(() => setStatus('rejected'));
        } else {
            setStatus('resolved');
        }
    }, []);
    if (status === 'resolved') {
        dayjs.locale(i18n.language);

        return (
            <Tracking
                trackingData={trackingData}
                updateTrackingData={updateTrackingData}
                estimatedTime={estimatedTime}
                refreshTime={dayjs().format('HH:mm')}
                contact={trackingConfig.contactInfos}
            >
                <MapWithTracking
                    trackingData={trackingData}
                    updateEstimatedTime={updateEstimatedTime}
                    trackingConfig={trackingConfig}
                    isTestMode={isTestMode}
                />
            </Tracking>
        );
    }

    if (status === 'rejected') return <Error />;

    return <Loader />;
};

export default withTranslation()(TrackingMap);
